// Glocke in der Kopfzeile (Design-Runde 2, Must-have 6): fällige Erinnerungen, verpasste Anrufe und Erwähnungen
// (@Name in Notizen) in einer Liste. Erinnerungen und verpasste Anrufe kommen aus den Aufgaben (wie die Karten unten
// rechts), Erwähnungen aus `GET /api/benachrichtigungen`. Die Zahl am Symbol zählt, was noch offen ist; ein Klick auf
// einen Eintrag öffnet den Lead, „Alle gelesen“ räumt die Erwähnungen ab.
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { benachrichtigungenApi } from "../api";
import { useEchtzeit } from "../api/echtzeit";
import { inboxApi, type Erinnerung } from "../api/inbox";
import type { Erwaehnung } from "../api/types";
import { relativ } from "../werkzeuge/zeit";
import { Ausklapp } from "./Ausklapp";
import { titel as erinnerungTitel, zeile1 } from "./ErinnerungBanner";
import { Icon } from "./Icon";
import "./erinnerung.css";

const TAKT_MS = 60_000;

export function Glocke() {
  const navigate = useNavigate();
  const [anker, setAnker] = useState<HTMLButtonElement | null>(null);
  const [offen, setOffen] = useState(false);
  const [erinnerungen, setErinnerungen] = useState<Erinnerung[]>([]);
  const [erwaehnungen, setErwaehnungen] = useState<Erwaehnung[]>([]);

  const laden = useCallback(async () => {
    const [a, b] = await Promise.allSettled([inboxApi.erinnerungen(), benachrichtigungenApi.liste()]);
    if (a.status === "fulfilled") setErinnerungen(a.value.data);
    if (b.status === "fulfilled") setErwaehnungen(b.value.data);
  }, []);

  useEffect(() => {
    void laden();
    const t = window.setInterval(() => void laden(), TAKT_MS);
    return () => window.clearInterval(t);
  }, [laden]);
  useEchtzeit(["activity.created", "task.created", "task.updated"], () => void laden());
  useEffect(() => {
    if (offen) void laden();
  }, [offen, laden]);

  const ungelesen = erwaehnungen.filter((e) => !e.gelesen);
  const zahl = erinnerungen.length + ungelesen.length;
  const titel = zahl ? `${zahl} ${zahl === 1 ? "Benachrichtigung" : "Benachrichtigungen"}` : "Keine neuen Benachrichtigungen";

  const zumLead = (leadId: string | null, ziel?: string) => {
    setOffen(false);
    navigate(leadId ? `/lead/${encodeURIComponent(leadId)}` : (ziel ?? "/inbox"));
  };
  const lesen = async (e: Erwaehnung) => {
    if (!e.gelesen) {
      try {
        const r = await benachrichtigungenApi.gelesen([e.id]);
        setErwaehnungen(r.data);
      } catch {
        /* beim nächsten Abruf wieder */
      }
    }
    zumLead(e.lead_id, e.art === "mail" ? "/inbox?reiter=emails" : undefined);
  };
  const alleGelesen = async () => {
    try {
      const r = await benachrichtigungenApi.gelesen();
      setErwaehnungen(r.data);
    } catch {
      /* egal */
    }
  };

  return (
    <>
      <button
        ref={setAnker}
        type="button"
        className={`glocke${offen ? " glocke--offen" : ""}`}
        title={titel}
        aria-label={titel}
        aria-haspopup="dialog"
        aria-expanded={offen}
        onClick={() => setOffen((o) => !o)}
      >
        <Icon name="glocke" groesse={16} />
        {zahl ? <span className="glocke__zahl">{zahl > 9 ? "9+" : zahl}</span> : null}
      </button>
      <Ausklapp anker={anker} offen={offen} onSchliessen={() => setOffen(false)} ausrichtung="end" breite={360} maxHoehe={520} rolle="dialog" className="glocke-menue">
        <div className="gl-kopf">
          <h2>Benachrichtigungen</h2>
          {ungelesen.length ? (
            <button type="button" className="textknopf" onClick={() => void alleGelesen()}>
              Alle gelesen
            </button>
          ) : null}
        </div>
        {!erinnerungen.length && !erwaehnungen.length ? <p className="gl-leer">Alles erledigt. Fällige Erinnerungen, verpasste Anrufe und Erwähnungen erscheinen hier.</p> : null}
        {erinnerungen.length ? (
          <section className="gl-teil" aria-label="Erinnerungen und verpasste Anrufe">
            <h3>Erinnerungen und Anrufe</h3>
            {erinnerungen.map((e) => (
              <button key={`${e.id}|${e.faellig_um}`} type="button" className="gl-zeile gl-zeile--offen" onClick={() => zumLead(e.lead_id, "/inbox?reiter=calls")}>
                <span className="gl-zeile__titel">{erinnerungTitel(e)}</span>
                <span className="gl-zeile__text">{zeile1(e)}</span>
              </button>
            ))}
          </section>
        ) : null}
        {erwaehnungen.length ? (
          <section className="gl-teil" aria-label="Erwähnungen und Mails">
            <h3>{erwaehnungen.every((e) => e.art === "mail") ? "Neue Mails" : erwaehnungen.some((e) => e.art === "mail") ? "Erwähnungen und Mails" : "Erwähnungen"}</h3>
            {erwaehnungen.map((e) => (
              <button key={e.id} type="button" className={`gl-zeile${e.gelesen ? "" : " gl-zeile--offen"}`} onClick={() => void lesen(e)}>
                <span className="gl-zeile__titel">
                  {e.art === "mail" ? "Neue E-Mail" : `${e.von ?? "Jemand"} hat dich erwähnt`}
                  {e.lead_name ? ` · ${e.lead_name}` : ""}
                </span>
                <span className="gl-zeile__text">{e.text}</span>
                {e.zeitpunkt ? <span className="gl-zeile__zeit">{relativ(e.zeitpunkt)}</span> : null}
              </button>
            ))}
          </section>
        ) : null}
      </Ausklapp>
    </>
  );
}
